/**
 * Lab member-management Detail tab shapes — Activity matrix, Balance, Login,
 * Commission. Activity figures come from Strix analytics (`/members/{id}/detail`,
 * `/agents/{id}/member-pnl`), not Lab beta-bq-api MemberActivity.
 *
 * Always returns a complete Lab-shaped payload for the selected member.
 * Missing live product lines fill as 0 (Lab empty day), never an "unavailable" hole.
 */
import type { Account } from '@/data/types'
import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import { availableToGive } from '@/lib/book'
export type ActivityPeriod =
  | 'today'
  | 'days3'
  | 'days7'
  | 'days30'
  | 'lifetime'
  | 'custom'

export const ACTIVITY_PERIODS: { id: ActivityPeriod; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'days3', label: '3 Days' },
  { id: 'days7', label: '7 Days' },
  { id: 'days30', label: '30 Days' },
  { id: 'lifetime', label: 'Lifetime' },
  { id: 'custom', label: 'Custom Dates' },
]

export type ActivityMetric =
  | 'win'
  | 'betfairComm'
  | 'commission'
  | 'pnl'
  | 'turnover'

export const ACTIVITY_METRICS: { id: ActivityMetric; label: string }[] = [
  { id: 'win', label: 'Win' },
  { id: 'betfairComm', label: 'Betfair Comm' },
  { id: 'commission', label: 'Commission' },
  { id: 'pnl', label: 'P&L' },
  { id: 'turnover', label: 'Turnover' },
]

/** Activity Select Games / Sub Games — these labels are removed from the options. */
const ACTIVITY_REMOVED_FILTER_NAMES = new Set([
  'uof',
  'racing',
  'transaction',
  'evenbet',
  'zerowin',
])

function activityFilterNameKey(name: string): string {
  return name.trim().toLowerCase().replace(/[\s_-]+/g, '')
}

/** True when a game/sub-game label must not appear in Activity filter selects. */
export function isActivityRemovedFilterName(name: string): boolean {
  const key = activityFilterNameKey(name)
  if (ACTIVITY_REMOVED_FILTER_NAMES.has(key)) return true
  // Live catalog sometimes uses "UOF Markets" etc.
  return key.startsWith('uof')
}

export type ActivityPeriodValues = Record<ActivityPeriod, number>

export type MemberActivityMatrix = {
  memberId: string
  memberScope: 'agent' | 'all-members'
  values: Record<ActivityMetric, ActivityPeriodValues>
}

export function emptyPeriodValues(): ActivityPeriodValues {
  return {
    today: 0,
    days3: 0,
    days7: 0,
    days30: 0,
    lifetime: 0,
    custom: 0,
  }
}

export function emptyActivityMatrix(
  memberId: string,
  memberScope: 'agent' | 'all-members' = 'agent',
): MemberActivityMatrix {
  return {
    memberId,
    memberScope,
    values: {
      win: emptyPeriodValues(),
      betfairComm: emptyPeriodValues(),
      commission: emptyPeriodValues(),
      pnl: emptyPeriodValues(),
      turnover: emptyPeriodValues(),
    },
  }
}

/** Fill every period of a metric with the same figure (demo / lifetime rollup). */
export function fillMetric(
  base: MemberActivityMatrix,
  metric: ActivityMetric,
  amount: number,
): MemberActivityMatrix {
  const period = emptyPeriodValues()
  for (const key of Object.keys(period) as ActivityPeriod[]) period[key] = amount
  return {
    ...base,
    values: { ...base.values, [metric]: period },
  }
}

/** One Lab Activity column — figures from Strix analytics period rollups. */
export type ActivityPeriodFigures = {
  win: number
  betfairComm: number
  commission: number
  pnl: number
  turnover: number
}

export function emptyPeriodFigures(): ActivityPeriodFigures {
  return { win: 0, betfairComm: 0, commission: 0, pnl: 0, turnover: 0 }
}

/**
 * Map analytics `MemberDetail.period` (or member-pnl rollup) into Lab columns.
 * Prefer memberNet; bookNet is book POV → invert (same as member-pnl rows /
 * mergeAnalyticsMemberDetail daily). Betfair/Commission stay 0 until those marts exist.
 */
export function figuresFromMemberPeriod(period?: {
  turnoverMinor?: string
  memberNetMinor?: string
  bookNetMinor?: string
}): ActivityPeriodFigures {
  const pnl =
    period?.memberNetMinor !== undefined && period.memberNetMinor !== ''
      ? fromMinorUnitsSigned(period.memberNetMinor)
      : period?.bookNetMinor !== undefined && period.bookNetMinor !== ''
        ? -fromMinorUnitsSigned(period.bookNetMinor)
        : 0
  return {
    win: Math.max(0, pnl),
    betfairComm: 0,
    commission: 0,
    pnl,
    turnover: fromMinorUnits(period?.turnoverMinor),
  }
}

export function setPeriodFigures(
  base: MemberActivityMatrix,
  period: ActivityPeriod,
  figures: ActivityPeriodFigures,
): MemberActivityMatrix {
  const next = emptyActivityMatrix(base.memberId, base.memberScope)
  next.values = {
    win: { ...base.values.win, [period]: figures.win },
    betfairComm: { ...base.values.betfairComm, [period]: figures.betfairComm },
    commission: { ...base.values.commission, [period]: figures.commission },
    pnl: { ...base.values.pnl, [period]: figures.pnl },
    turnover: { ...base.values.turnover, [period]: figures.turnover },
  }
  return next
}

/** YYYY-MM-DD in local calendar (matches Lab Activity date pickers → analytics from/to). */
export function isoDayLocal(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function addDaysLocal(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(y!, m! - 1, d!)
  dt.setDate(dt.getDate() + delta)
  return isoDayLocal(dt)
}

/**
 * Lab Activity columns → analytics `from`/`to` windows.
 * Custom uses the filter pickers; lifetime starts at a fixed floor (no account-created on UI).
 */
export function activityPeriodRanges(
  customFrom: string,
  customTo: string,
  today: string = isoDayLocal(),
): Record<ActivityPeriod, { from: string; to: string }> {
  const from = customFrom.slice(0, 10)
  const to = customTo.slice(0, 10)
  return {
    today: { from: today, to: today },
    days3: { from: addDaysLocal(today, -2), to: today },
    days7: { from: addDaysLocal(today, -6), to: today },
    days30: { from: addDaysLocal(today, -29), to: today },
    // ponytail: no createdAt on the Activity tab — floor is far enough for mart history.
    lifetime: { from: '2015-01-01', to: today },
    custom: { from: from <= to ? from : to, to: from <= to ? to : from },
  }
}

export type BalanceLine = {
  id: string
  label: string
  value: number
  /** Hide for non-players when Lab gate was player-only. */
  playerOnly?: boolean
}

/**
 * Lab Balance tab rows for the selected member.
 * Product NE from Agent-API player get. Premium BM omitted (not in Strix yet).
 * Horse racing stays 0 until racebook schema exists.
 */
export function balanceLinesFromAccount(account: Account): BalanceLine[] {
  const free = availableToGive(account.balance, account.exposure)
  const sports =
    account.exposureSportsbook !== undefined ? account.exposureSportsbook : account.exposure
  const lines: BalanceLine[] = [
    { id: 'ne-sportsbook', label: 'Net Exposure (Sports Book)', value: sports },
    {
      id: 'ne-cards',
      label: 'Net Exposure (Live Cards)',
      value: account.exposureLiveCards ?? 0,
      playerOnly: true,
    },
    {
      id: 'ne-casino',
      label: 'Net Exposure (Live Casino)',
      value: account.exposureLiveCasino ?? 0,
      playerOnly: true,
    },
    {
      id: 'ne-racing',
      label: 'Net Exposure (Horse Racing)',
      value: account.exposureHorseRacing ?? 0,
      playerOnly: true,
    },
    { id: 'balance-up', label: 'Balance Up', value: Math.max(0, account.settlement) },
    { id: 'balance-down', label: 'Balance Down', value: Math.max(0, -account.settlement) },
    { id: 'credit-limit', label: 'Credit Limit', value: account.creditLimit },
    { id: 'available', label: 'Available Credit', value: free },
  ]
  if (account.role !== 'player') {
    lines.push({
      id: 'distributed',
      label: 'Total credit given to downline',
      value: account.distributedBalance,
    })
  }
  return lines.filter((line) => !line.playerOnly || account.role === 'player')
}

export type LoginHistoryRow = {
  id: string
  name: string
  at: string
  ip: string
  device: string
  browser: string
  os: string
  isp: string
  city: string
  /** Lab IpQualityScore subset — used by IP detail modal. */
  qualityScore?: Record<string, unknown>
}

/** @deprecated Prefer Agent-API `getUserLog` — kept for fixture/activity fallbacks. */
export function loginHistoryFromActivity(input: {
  account: Account
  events: { id: string; at: string; summary: string; kind: string; detail?: string }[]
}): LoginHistoryRow[] {
  return input.events
    .filter((e) => e.kind === 'login')
    .map((e) => ({
      id: e.id,
      name: input.account.username,
      at: e.at,
      ip: '',
      device: e.detail?.includes('device') ? e.detail : '',
      browser: '',
      os: '',
      isp: '',
      city: '',
    }))
}

/**
 * Lab `member-commission` tile: when upline > 0, template prefixes `-`
 * before `indianCurrency` (debit-style). Zero / already-negative stay as-is.
 */
export function labMemberCommissionDisplay(upline: number): number {
  return upline > 0 ? -upline : upline
}

/* ---------------------------------------------------- transfer statements -- */

/** Lab member-transfer-New rejects searches wider than this. */
export const LAB_TRANSFER_MAX_DAYS = 15

/**
 * Lab `memberTransferNew`: if the viewed member is the payee (`toUser`), flip
 * the amount so received credit reads negative (Player Loss on the legend).
 */
export function labTransferSignedAmount(
  amount: number,
  toUsername: string | null | undefined,
  memberUsername: string,
): number {
  return toUsername === memberUsername ? -amount : amount
}

/** Lab Total Net Share = given − taken for the viewed seat (= −analytics net). */
export function labTransferNetShare(given: number, taken: number): number {
  return Math.round((given - taken) * 100) / 100
}

/** Inclusive calendar-day span matching Lab `Math.ceil(ms / day)`. */
export function labTransferRangeDays(fromDay: string, toDay: string): number {
  const from = Date.parse(`${fromDay.slice(0, 10)}T00:00:00`)
  const to = Date.parse(`${toDay.slice(0, 10)}T00:00:00`)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0
  return Math.ceil(Math.abs(to - from) / 86_400_000)
}

/** Lab Payer/Payee cell: `fromUser ( toUser )`. */
export function labTransferPayerPayee(
  fromUsername: string | null | undefined,
  toUsername: string | null | undefined,
): string {
  const from = fromUsername?.trim() || '—'
  const to = toUsername?.trim() || '—'
  return `${from} ( ${to} )`
}
